import { useState, useEffect, useRef } from 'react';
import { buildSupportReply, canCreateSupportTicket, type SupportAssistantAction } from '../core/help/supportAssistant';
import type { Language } from '../types';
import { isValidNavTarget } from '../core/help/navRegistry';
import { waitForTypingDelay } from '../core/help/typingDelay';
import type { AssistReplySource } from '../services/help/assistUser';
import { useSupportChat } from './useSupportChat';
import { useTickets } from './useTickets';
import { useLiveSupport } from './useLiveSupport';
import { useSupportLiveSession } from './useSupportLiveSession';
import { useNetworkStatus } from './useNetworkStatus';
import { usePresentAppError } from './usePresentAppError';
import { useNotifs } from '../contexts/index';
import {
  newHelpChatMessageId,
  useHelpChatSession,
} from '../contexts/HelpChatSessionContext';
import { submitSupportFeedback } from '../services/help/supportFeedback';

export function useHelpChatController({
  t,
  lang,
  user,
  onNavigate,
  liveSupportEnabled = true,
  networkOnline: networkOnlineProp,
}: {
  t: Record<string, string>;
  lang: string;
  user: { id?: string; houseId?: string; role?: string } | null | undefined;
  onNavigate?: (target: string) => void;
  liveSupportEnabled?: boolean;
  networkOnline?: boolean;
}) {
  const { assistUser, getTraceContext } = useSupportChat();
  const { createTicket } = useTickets();
  const { requestLiveChat, sendLiveMessage } = useLiveSupport();
  const { networkOnline: networkFromHook } = useNetworkStatus();
  const networkOnline = networkOnlineProp ?? networkFromHook;
  const presentAppError = usePresentAppError();
  const { push } = useNotifs();
  const session = useHelpChatSession();
  const [typing, setTyping] = useState(false);
  const [liveSending, setLiveSending] = useState(false);
  const [replySource, setReplySource] = useState<AssistReplySource | null>(null);
  const [ratedMessageIds, setRatedMessageIds] = useState<Set<string>>(() => new Set());
  const liveStatusRef = useRef<string | null>(null);
  const {
    messages, draft, phase, lastCategory, lastPriority, liveSessionId,
    setDraft, setPhase, setLiveSessionId, setLastCategory, setLastPriority,
    appendMessages, sendingLockRef,
  } = session;

  const inLiveHandoff = phase === 'queued_live' || phase === 'live';
  const { session: liveSession, messages: liveMessages } = useSupportLiveSession(
    user?.houseId ?? '',
    liveSessionId,
    inLiveHandoff,
  );

  useEffect(() => {
    const status = liveSession?.status ?? null;
    if (!status || status === liveStatusRef.current) return;
    liveStatusRef.current = status;

    if (status === 'active' && phase === 'queued_live') {
      setPhase('live');
      return;
    }
    if (status === 'closed') {
      appendMessages({
        id: newHelpChatMessageId(),
        role: 'assistant',
        text: t.helpLiveClosed,
      });
      setPhase('idle');
      setLiveSessionId(null);
      liveStatusRef.current = null;
    }
  }, [
    liveSession?.status,
    phase,
    setPhase,
    setLiveSessionId,
    appendMessages,
    t.helpLiveClosed,
  ]);

  const busy = phase === 'answering' || phase === 'creating_ticket' || typing || liveSending;

  const appendAssistant = async (
    replyText: string,
    startedAt: number,
    extra?: {
      actions?: SupportAssistantAction[];
      handoffOffer?: boolean;
    },
  ) => {
    setTyping(true);
    await waitForTypingDelay(replyText, startedAt);
    setTyping(false);
    appendMessages({
      id: newHelpChatMessageId(),
      role: 'assistant',
      text: replyText,
      actions: extra?.actions,
      handoffOffer: extra?.handoffOffer,
    });
  };

  const historyForAssist = () => messages.map((m) => ({ role: m.role, text: m.text }));

  const send = async () => {
    if (!draft.trim() || busy || sendingLockRef.current) return;

    if (inLiveHandoff && liveSessionId && user?.houseId) {
      const text = draft.trim();
      setDraft('');
      setLiveSending(true);
      try {
        await sendLiveMessage({
          sessionId: liveSessionId,
          houseId: user.houseId,
          text,
        });
      } catch (err) {
        presentAppError(err, { t, push, context: { feature: 'helpChatLive' } });
        setDraft(text);
        appendMessages({
          id: newHelpChatMessageId(),
          role: 'assistant',
          text: t.helpLiveSendFailed,
        });
      } finally {
        setLiveSending(false);
      }
      return;
    }

    sendingLockRef.current = true;
    const text = draft.trim();
    setDraft('');
    appendMessages({ id: newHelpChatMessageId(), role: 'user', text });
    setPhase('answering');
    setTyping(true);
    const hist = [...historyForAssist(), { role: 'user', text }];
    try {
      const started = Date.now();
      const res = await assistUser({
        message: text,
        houseId: user?.houseId,
        role: user?.role,
        lang: lang as Language,
        history: hist,
        networkOnline,
        traceId: getTraceContext().traceId,
      });
      setReplySource(res.source);
      setLastCategory(res.category ?? 'other');
      setLastPriority(res.priority ?? 'normal');
      await appendAssistant(res.reply, started, {
        actions: res.actions,
        handoffOffer: res.escalate || res.handoffOffer,
      });
      setPhase(res.escalate || res.handoffOffer ? 'offering_handoff' : 'idle');
    } catch (err) {
      presentAppError(err, { t, push, context: { feature: 'helpChat' } });
      const fallback = buildSupportReply({
        message: text,
        role: user?.role,
        lang: lang as 'en' | 'es',
        history: hist,
      });
      setReplySource(networkOnline ? 'degraded' : 'local');
      await appendAssistant(fallback.reply, Date.now(), {
        actions: fallback.actions,
        handoffOffer: fallback.escalate || fallback.handoffOffer,
      });
      setPhase(fallback.escalate ? 'offering_handoff' : 'idle');
    } finally {
      setTyping(false);
      sendingLockRef.current = false;
    }
  };

  const canTicket = canCreateSupportTicket(user?.role) && Boolean(user?.houseId);

  const confirmTicket = async (subjectText?: string) => {
    if (!canTicket || sendingLockRef.current) return;
    const subject = (subjectText || draft.trim()
      || messages.filter((m) => m.role === 'user').at(-1)?.text || '').slice(0, 80);
    if (!subject) {
      appendMessages({ id: newHelpChatMessageId(), role: 'assistant', text: t.helpOpenTicketEmpty });
      return;
    }
    sendingLockRef.current = true;
    setPhase('creating_ticket');
    try {
      await createTicket({
        subject,
        houseId: user!.houseId!,
        message: subjectText || draft.trim() || subject,
        traceId: getTraceContext().traceId,
        category: lastCategory,
        priority: lastPriority,
        clientRequestId: `tk_${user?.id}_${subject.slice(0, 40)}_${Date.now()}`,
      });
      await appendAssistant(t.helpTicketCreated, Date.now());
      setPhase('idle');
      setDraft('');
    } catch {
      setPhase('error');
      await appendAssistant(t.helpHandoffTicketFailed, Date.now());
      setPhase('offering_handoff');
    } finally {
      sendingLockRef.current = false;
    }
  };

  const openTicketShortcut = async () => {
    if (!canTicket || busy) return;
    const text = draft.trim();
    if (!text) {
      appendMessages({ id: newHelpChatMessageId(), role: 'assistant', text: t.helpOpenTicketEmpty });
      return;
    }
    appendMessages({ id: newHelpChatMessageId(), role: 'user', text });
    setDraft('');
    await confirmTicket(text);
  };

  const startLive = async () => {
    if (!liveSupportEnabled || !user?.houseId || sendingLockRef.current) return;
    sendingLockRef.current = true;
    setPhase('queued_live');
    try {
      const res = await requestLiveChat({
        houseId: user.houseId,
        summary: messages.filter((m) => m.role === 'user').slice(-3)
          .map((m) => m.text).join(' | ').slice(0, 280),
      });
      setLiveSessionId(res.id || null);
      liveStatusRef.current = res.status === 'queued' || res.status === 'active' ? res.status : null;
      if (res.status === 'queued' || res.status === 'active') {
        await appendAssistant(
          res.status === 'active' ? t.helpLiveConnected : t.helpLiveQueued,
          Date.now(),
        );
        setPhase(res.status === 'active' ? 'live' : 'queued_live');
      } else {
        await appendAssistant(t.helpLiveUnavailable, Date.now());
        setPhase('offering_handoff');
      }
    } catch {
      await appendAssistant(t.helpLiveUnavailable, Date.now());
      setPhase('offering_handoff');
    } finally {
      sendingLockRef.current = false;
    }
  };

  const handleNavigate = (target: string) => {
    if (!onNavigate || !isValidNavTarget(target, user?.role)) return;
    onNavigate(target);
  };

  const rateReply = async (messageId: string, rating: 'up' | 'down') => {
    if (ratedMessageIds.has(messageId)) return;
    setRatedMessageIds((prev) => new Set(prev).add(messageId));
    try {
      await submitSupportFeedback({
        rating,
        messageId,
        houseId: user?.houseId,
        traceId: getTraceContext().traceId,
      });
      push({
        cat: 'system',
        sev: 'info',
        urgent: false,
        title: t.helpFeedbackThanks,
        body: '',
      });
    } catch (err) {
      presentAppError(err, { t, push, context: { feature: 'helpFeedback' } });
      setRatedMessageIds((prev) => {
        const next = new Set(prev);
        next.delete(messageId);
        return next;
      });
    }
  };

  return {
    messages, draft, setDraft, phase, setPhase, typing, busy, liveSessionId,
    liveMessages, liveSession,
    replySource, networkOnline, ratedMessageIds,
    canTicket, canSend: Boolean(draft.trim()) && !busy,
    send, confirmTicket, openTicketShortcut, startLive, handleNavigate, rateReply,
  };
}
